import {
    AutoRegistrationEnabledConnectionTemplateField,
    AutoSubscriptionEnabledConnectionTemplateField,
    ConnectionTemplateConnectionsField,
    GuidelinesConnectionTemplateField,
    IConnectionTemplateConnectionGroupsField,
    IConnectionTemplateDTO,
    IConnectionTemplateField,
    IRoleConnectionInput,
    IRoleConnectionsValue,
    RoleConnectionsConnectionTemplateField,
    ICreateConnectionTemplateDTO,
    IKnovioConnection,
    PodiumAccessTypeConnectionTemplateField,
    PodiumAttendeesEnabledConnectionTemplateField,
    PodiumChatEnabledConnectionTemplateField,
    PodiumLinkAction,
    PodiumLinkEnabledConnectionTemplateField,
    PodiumParticipantsEnabledConnectionTemplateField,
    PodiumParticipantsLabelConnectionTemplateField,
    PodiumPresenterViewEnabledConnectionTemplateField,
    PodiumQuestionAnswerEnabledConnectionTemplateField,
    TConnection,
    TConnectionTemplateAction,
} from '../../../../api/blueprints/connection.template.types';
import { ActionType, BlueprintFieldType, BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { FieldKey } from './connection-template-form.config';
import { ConnectionVersion } from '../../../../constants/common.constants';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../global/rich-text-editor/utils/transpiler';
import { PodiumAccessType } from '../../../../api/podium.dao';
import { fromRoleConnectionFieldValue, toRoleConnectionFieldValue } from './connection-template-role-connections/role-connections.utils';

export type Values = { [key: string]: unknown };
export type IConnectionDetails = { name: string; value: string };

export interface ConnectionTemplateValues extends Values {
    accountId: number;
    name: string;
    description?: string;
}

export class ConnectionTemplateMapper {
    public static toConnectionTemplateDTO(values: ConnectionTemplateValues): ICreateConnectionTemplateDTO | IConnectionTemplateDTO {
        const dto = { type: BlueprintType.CONNECTION } as ICreateConnectionTemplateDTO;
        dto.accountId = values.accountId;
        dto.name = values[FieldKey.NAME].trim();
        dto.description = values[FieldKey.DESCRIPTION].trim();
        dto.fields = ConnectionTemplateMapper.toConnectTemplateFields(values);
        dto.actions = ConnectionTemplateMapper.toConnectTemplateActions(dto.fields);
        dto.connectionVersion = values[FieldKey.CONNECTION_VERSION] ? ConnectionVersion.V2 : ConnectionVersion.V1;

        return dto;
    }

    public static toEditConnectionTemplateDTO(values: ConnectionTemplateValues): ICreateConnectionTemplateDTO | IConnectionTemplateDTO {
        const dto = ConnectionTemplateMapper.toConnectionTemplateDTO(values);

        dto.blueprintId = values.blueprintId as string;
        dto.createdAt = values.createdAt as string;
        dto.updatedAt = values.updatedAt as string;

        return dto;
    }

    public static toConnectionTemplateValuesFromConnectionTemplateDTO(dto: IConnectionTemplateDTO): ConnectionTemplateValues {
        const values = {} as ConnectionTemplateValues;
        values.accountId = dto.accountId;
        values.name = dto.name;
        values.description = dto.description;
        values.createdAt = dto.createdAt;
        values.updatedAt = dto.updatedAt;
        values.connectionVersion = dto.connectionVersion === ConnectionVersion.V2;

        dto.fields?.forEach(field => {
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            const fieldValue = this.transpileIfToCKEditor(field.name, field.value);
            values[field.name] = field.name === 'roleConnections'
                ? fromRoleConnectionFieldValue(fieldValue as IRoleConnectionInput[])
                : fieldValue;
        });

        return values;
    }

    private static toConnectTemplateFields(values: ConnectionTemplateValues): IConnectionTemplateField[] {
        return ConnectionTemplateMapper.fieldMapperList
            .reduce((fields, mapper) => {
                const field = mapper(values);
                return field ? fields.concat(field) : fields;
            }, [] as IConnectionTemplateField[])
            ;
    }

    private static toConnectTemplateActions(values: IConnectionTemplateField[]): TConnectionTemplateAction[] {
        return ConnectionTemplateMapper.actionMapperList
            .reduce((actions, mapper) => {
                const action = mapper(values);
                return action ? actions.concat(action) : actions;
            }, [] as TConnectionTemplateAction[]);
    }

    private static get fieldMapperList(): ((values: ConnectionTemplateValues) => IConnectionTemplateField | null)[] {
        return [
            ConnectionTemplateMapper.mapMeetingGuidelinesField,
            ConnectionTemplateMapper.mapConnectionsField,
            ConnectionTemplateMapper.mapConnectionGroupsAction,
            ConnectionTemplateMapper.mapAutoRegistrationEnabledField,
            ConnectionTemplateMapper.mapAutoSubscriptionEnabledField,
            ConnectionTemplateMapper.mapPodiumLinkEnabledField,
            ConnectionTemplateMapper.mapPodiumPresenterViewEnabledField,
            ConnectionTemplateMapper.mapPodiumAccessTypeField,
            ConnectionTemplateMapper.mapPodiumParticipantsEnabledField,
            ConnectionTemplateMapper.mapPodiumParticipantsLabelField,
            ConnectionTemplateMapper.mapPodiumAttendeesEnabledField,
            ConnectionTemplateMapper.mapPodiumChatEnabledField,
            ConnectionTemplateMapper.mapPodiumQuestionAnswerEnabledField,
            ConnectionTemplateMapper.mapRoleConnectionsField,
        ];
    }

    private static get actionMapperList(): ((values: IConnectionTemplateField[]) => TConnectionTemplateAction | null)[] {
        return [
            ConnectionTemplateMapper.mapConnectionsAction,
            ConnectionTemplateMapper.mapPodiumAction
        ];
    }

    private static mapMeetingGuidelinesField(values: ConnectionTemplateValues): GuidelinesConnectionTemplateField | null {
        if (!values[FieldKey.GUIDELINES]) return null;
        // Since CKEditor works with classes, but data is stored with inline styles,
        // all CKEditor values need to be converted to the appropriate format
        const transpiledValue = transpileClassesToInlineStyles(values[FieldKey.GUIDELINES] as string);
        return { name: 'guidelines', value: transpiledValue.trimEnd(), type: BlueprintFieldType.FORM_PREFILL };
    }

    private static mapAutoRegistrationEnabledField(values: ConnectionTemplateValues): AutoRegistrationEnabledConnectionTemplateField | null {
        if (!values[FieldKey.AUTO_REGISTRATION_ENABLED]) return null;
        return { name: 'autoRegistrationEnabled', value: values[FieldKey.AUTO_REGISTRATION_ENABLED] as boolean, type: BlueprintFieldType.FORM_PREFILL };
    }

    private static mapConnectionsField(values: ConnectionTemplateValues): ConnectionTemplateConnectionsField | null {
        if (!(values.connections as ConnectionTemplateConnectionsField[]).length) return null;
        return {
            name: 'connections',
            value: (values.connections as TConnection[]).map(connection => ({ ...connection })),
            type: BlueprintFieldType.CONNECTION_ACTION_INPUT
        };
    }

    private static mapConnectionsAction(values: IConnectionTemplateField[]): TConnectionTemplateAction | null {
        if (!values.find(x => x.name === 'connections')) return;
        return { order: 0, type: ActionType.CONNECTION_TYPE };
    }

    private static mapPodiumAction(values: IConnectionTemplateField[]): PodiumLinkAction | null {
        if (!values.find(x => x.name === 'podiumLinkEnabled')) return;
        return { order: 1, type: ActionType.PODIUM_LINK };
    }

    private static mapAutoSubscriptionEnabledField(values: ConnectionTemplateValues): AutoSubscriptionEnabledConnectionTemplateField | null {
        if (!values[FieldKey.AUTO_SUBSCRIPTION_ENABLED]) return null;
        return { name: 'autoSubscriptionEnabled', value: values[FieldKey.AUTO_SUBSCRIPTION_ENABLED] as boolean, type: BlueprintFieldType.FORM_PREFILL };
    }

    private static mapConnectionGroupsAction(values: ConnectionTemplateValues): IConnectionTemplateConnectionGroupsField | null {
        const connectionGroups = values.connectionGroups as IConnectionTemplateConnectionGroupsField[] | undefined;
        if (!connectionGroups.length) return;
        return {
            name: 'connectionGroups',
            type: BlueprintFieldType.CONNECTION_GROUP_ACTION_INPUT,
            value: connectionGroups.map(group => {
                return {
                    ...group
                };
            }),
        } as unknown as IConnectionTemplateConnectionGroupsField;
    }

    private static transpileIfToCKEditor = (name: string, value: IConnectionTemplateField['value']): IConnectionTemplateField['value'] => {
        if (name === FieldKey.GUIDELINES && typeof value === 'string') {
            return transpileInlineStylesToClasses(value);
        } else if (name === 'connections') {
            return this.transpileConnectionPropsToCKEditor(value as TConnection[]);
        } else {
            return value;
        }
    };

    private static transpileConnectionPropsToCKEditor = (connections: TConnection[]): TConnection[] => {
        return connections?.map((item: IKnovioConnection) => {
            if (item.standByMessage) {
                return {
                    ...item,
                    standByMessage: transpileInlineStylesToClasses(item.standByMessage)
                };
            } else {
                return item;
            }
        }) || [];
    };

    private static mapPodiumLinkEnabledField(values: ConnectionTemplateValues): PodiumLinkEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_LINK_ENABLED]) return null;
        return { name: 'podiumLinkEnabled', value: values[FieldKey.PODIUM_LINK_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumPresenterViewEnabledField(values: ConnectionTemplateValues): PodiumPresenterViewEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_LINK_ENABLED] || !values[FieldKey.PODIUM_PRESENTER_VIEW_ENABLED]) return null;
        return { name: 'podiumPresenterViewEnabled', value: values[FieldKey.PODIUM_PRESENTER_VIEW_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumAccessTypeField(values: ConnectionTemplateValues): PodiumAccessTypeConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_LINK_ENABLED]) return null;
        return { name: 'podiumAccessType', value: values[FieldKey.PODIUM_ACCESS_TYPE] as PodiumAccessType, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumParticipantsEnabledField(values: ConnectionTemplateValues): PodiumParticipantsEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_PARTICIPANTS_ENABLED]) return null;
        return { name: 'podiumParticipantsEnabled', value: values[FieldKey.PODIUM_PARTICIPANTS_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumAttendeesEnabledField(values: ConnectionTemplateValues): PodiumAttendeesEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_ATTENDEES_ENABLED]) return null;
        return { name: 'podiumAttendeesEnabled', value: values[FieldKey.PODIUM_ATTENDEES_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumChatEnabledField(values: ConnectionTemplateValues): PodiumChatEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_CHAT_ENABLED]) return null;
        return { name: 'podiumChatEnabled', value: values[FieldKey.PODIUM_CHAT_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumParticipantsLabelField(values: ConnectionTemplateValues): PodiumParticipantsLabelConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_PARTICIPANTS_ENABLED]) return null;
        const label = (values[FieldKey.PODIUM_PARTICIPANTS_LABEL] as string)?.trim();
        if (!label) return null;
        return { name: 'podiumParticipantsLabel', value: label, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapPodiumQuestionAnswerEnabledField(values: ConnectionTemplateValues): PodiumQuestionAnswerEnabledConnectionTemplateField | null {
        if (!values[FieldKey.PODIUM_QUESTION_ANSWER_ENABLED]) return null;
        return { name: 'podiumQuestionAnswerEnabled', value: values[FieldKey.PODIUM_QUESTION_ANSWER_ENABLED] as boolean, type: BlueprintFieldType.PODIUM_LINK_ACTION_INPUT };
    }

    private static mapRoleConnectionsField(values: ConnectionTemplateValues): RoleConnectionsConnectionTemplateField | null {
        if (!values.roleConnections) return null;
        return {
            name: 'roleConnections',
            value: toRoleConnectionFieldValue(values.roleConnections as IRoleConnectionsValue),
            type: BlueprintFieldType.ROLE_CONNECTION_ACTION_INPUT
        };
    }
}
